import { Injectable, signal } from '@angular/core';

import { PRIMARY_COURSE_ID } from '../course.constants';
import { normalizeCourseId } from '../course-id.aliases';
import { CourseExerciseStatus } from '../models/course-content.models';
import {
  CompletionStatus,
  LearnerCourseProgress,
  StableId,
  TaskStatus,
  createStableId,
} from '../models/lernapp.models';
import { LocalStorageLearnerStorage } from './learner-storage';

const VALID_VOUCHER_CODE = '90001';

export type AccountSetupPath =
  | 'new'
  | 'new-skip'
  | 'existing'
  | 'existing-beginner'
  | 'existing-experienced'
  | null;
export type ParticipationStatus = 'active' | 'new' | null;
export type HasVoucherAnswer = boolean | null;

export interface QuizState {
  selectedOptionIds: string[];
  evaluated: boolean;
  passed: boolean;
}

export interface CategorizationCardResult {
  selectedCategoryId: string;
  correct: boolean;
}

export interface CategorizationQuizState {
  assignments: Record<string, string>;
  results: Record<string, CategorizationCardResult>;
  currentCardIndex: number;
  completed: boolean;
  scorePercent: number;
}

export interface LernbausteinOpenRequest {
  moduleId: string;
  lessonId: string;
  lernbausteinId: string;
  targetId?: string;
  requestId: number;
}

export interface NextCourseLessonLocation {
  moduleId: string;
  lessonId: string;
}

export interface LatestVisitedLessonLocation {
  moduleId: string;
  lessonId: string;
  viewedAt: Date | null;
}

export interface LastCourseLocation {
  courseId: string;
  moduleId: string;
  lessonId: string;
  blockId: string;
}

export interface CourseProgressStructure {
  modules: ReadonlyArray<{
    id: string;
    lessons: ReadonlyArray<{
      id: string;
      blockIds: ReadonlyArray<string>;
    }>;
  }>;
  tasks?: ReadonlyArray<{
    id: string;
    moduleId: string;
    lessonId: string;
    competencyId?: string | null;
  }>;
  competencyLevels?: ReadonlyArray<{
    level: number;
    competencies: ReadonlyArray<{
      id: string;
      taskIds: ReadonlyArray<string>;
    }>;
  }>;
}

export type CompetencyProgressStatus = 'locked' | 'available' | 'achieved';

interface ExerciseState {
  checkedInstructionIndexes: number[];
  status: CourseExerciseStatus;
}

type ExerciseStateMap = Record<string, Record<number, ExerciseState>>;

export interface ReviewQuestionStats {
  attempts: number;
  correct: number;
  lastAnsweredAt: string;
  lastCorrect: boolean;
  moduleId?: string;
  competencyId?: string;
}

interface ReviewState {
  questionStats: Record<string, ReviewQuestionStats>;
  completedRounds: Record<string, number>;
}

interface LearnerActivityState {
  voucherValidated: boolean;
  accountSetupPath: AccountSetupPath;
  githubVisibilityConfirmed: boolean;
  visitedModuleIds: string[];
  completedSubtasks: Record<string, number[]>;
  activeLernbausteinIds: Record<string, string>;
  lastCourseLocation: LastCourseLocation | null;
  quizzes: Record<string, QuizState>;
  categorizationQuizzes: Record<string, CategorizationQuizState>;
  exercises: ExerciseStateMap;
  review: ReviewState;
}

@Injectable({ providedIn: 'root' })
export class LearnerStateService {
  private readonly storage = new LocalStorageLearnerStorage();
  private readonly _courseContext = signal<StableId>(createStableId(PRIMARY_COURSE_ID));
  private readonly _progress = signal(this.loadOrCreateProgress(this._courseContext()));
  private readonly _activity = signal(this.loadActivity(this._courseContext()));
  private readonly _participationStatus = signal<ParticipationStatus>(null);
  private readonly _hasVoucherAnswer = signal<HasVoucherAnswer>(null);
  private courseStructure: CourseProgressStructure = { modules: [] };

  readonly courseContext = this._courseContext.asReadonly();
  readonly accountSetupPath = signal<AccountSetupPath>(this._activity().accountSetupPath);
  readonly githubVisibilityConfirmed = signal(this._activity().githubVisibilityConfirmed);
  readonly voucherValidated = signal(this._activity().voucherValidated);
  readonly participationStatus = this._participationStatus.asReadonly();
  readonly hasVoucherAnswer = this._hasVoucherAnswer.asReadonly();
  readonly lernbausteinOpenRequest = signal<LernbausteinOpenRequest | null>(null);
  private nextLernbausteinRequestId = 0;

  setCourseContext(courseId: string): void {
    const normalizedCourseId = createStableId(normalizeCourseId(courseId));
    if (normalizedCourseId === this._courseContext()) {
      return;
    }

    this._courseContext.set(normalizedCourseId);
    this._progress.set(this.loadOrCreateProgress(normalizedCourseId));
    this._activity.set(this.loadActivity(normalizedCourseId));
    this.syncActivitySignals();
    this._participationStatus.set(null);
    this._hasVoucherAnswer.set(null);
    this.courseStructure = { modules: [] };
  }

  isModuleDone(moduleId: string): boolean {
    return this.isModuleResolved(moduleId);
  }

  isModuleResolved(moduleId: string): boolean {
    const status = this.getModuleStatus(moduleId);
    return status === 'completed' || status === 'skipped';
  }

  isModuleSkipped(moduleId: string): boolean {
    return this.getModuleStatus(moduleId) === 'skipped';
  }

  getModuleStatus(moduleId: string): CompletionStatus {
    return this._progress().moduleProgress.find((entry) => entry.moduleId === moduleId)?.status
      ?? 'not-started';
  }

  hasModuleBeenSeen(moduleId: string): boolean {
    if (this._activity().visitedModuleIds.includes(moduleId)) {
      return true;
    }

    const moduleProgress = this._progress().moduleProgress.find(
      (entry) => entry.moduleId === moduleId,
    );
    if (!moduleProgress) {
      return false;
    }

    return moduleProgress.status !== 'not-started'
      || moduleProgress.lectionProgress.some(
        (lesson) =>
          lesson.status !== 'not-started'
          || lesson.lernbausteinProgress.some((block) => block.status !== 'not-started'),
      );
  }

  getCompletedCount(): number {
    return this._progress().moduleProgress.filter(
      (entry) => entry.status === 'completed' || entry.status === 'skipped',
    ).length;
  }

  getCompletedModuleIds(): StableId[] {
    return this._progress().moduleProgress
      .filter((entry) => entry.status === 'completed' || entry.status === 'skipped')
      .map((entry) => entry.moduleId);
  }

  getFirstIncompleteModuleId(): StableId | null {
    return this._progress().moduleProgress.find(
      (entry) => entry.status !== 'completed' && entry.status !== 'skipped',
    )?.moduleId ?? null;
  }

  getNextCourseLessonLocation(): NextCourseLessonLocation | null {
    const lessons = this.courseStructure.modules.flatMap((module) =>
      module.lessons.map((lesson) => ({ moduleId: module.id, lessonId: lesson.id })),
    );
    if (lessons.length === 0) {
      return null;
    }

    let lastResolvedLessonIndex = -1;
    for (const module of this.courseStructure.modules) {
      let moduleLastLessonIndex = -1;
      lessons.forEach((lesson, index) => {
        if (lesson.moduleId === module.id) {
          moduleLastLessonIndex = index;
        }
      });
      if (moduleLastLessonIndex < 0) {
        continue;
      }

      if (this.isModuleResolved(module.id)) {
        lastResolvedLessonIndex = Math.max(lastResolvedLessonIndex, moduleLastLessonIndex);
        continue;
      }

      for (const lesson of module.lessons) {
        if (this.isLessonDone(module.id, lesson.id)) {
          const lessonIndex = lessons.findIndex(
            (candidate) =>
              candidate.moduleId === module.id && candidate.lessonId === lesson.id,
          );
          lastResolvedLessonIndex = Math.max(lastResolvedLessonIndex, lessonIndex);
        }
      }
    }

    return lessons[lastResolvedLessonIndex + 1] ?? lessons[lessons.length - 1];
  }

  markModuleCompleted(moduleId: string): void {
    const progress = this._progress();
    const moduleProgress = progress.moduleProgress.map((entry) =>
      entry.moduleId === moduleId
        ? {
            ...entry,
            status: 'completed' as const,
            statusBeforeSkip: undefined,
            milestoneStatus: 'completed' as const,
            milestoneCompletedAt: new Date(),
          }
        : entry,
    );
    this.saveProgress({ ...progress, moduleProgress });
  }

  markModuleSkipped(moduleId: string): void {
    const progress = this._progress();
    const moduleProgress = progress.moduleProgress.map((entry) =>
      entry.moduleId !== moduleId || entry.status === 'skipped' ? entry : {
        ...entry,
        statusBeforeSkip: entry.status,
        status: 'skipped' as const,
      },
    );
    this.saveProgress({ ...progress, moduleProgress });
  }

  resetModuleSkip(moduleId: string): void {
    if (!this.isModuleSkipped(moduleId)) {
      return;
    }
    const moduleProgress = this._progress().moduleProgress.find(
      (entry) => entry.moduleId === moduleId,
    );
    const started = moduleProgress?.lectionProgress.some(
      (lesson) => lesson.status !== 'not-started'
        || lesson.lernbausteinProgress.some((block) => block.status !== 'not-started'),
    ) ?? false;
    const restoredStatus = moduleProgress?.statusBeforeSkip === 'completed'
      ? 'completed'
      : started
        ? 'in-progress'
        : moduleProgress?.statusBeforeSkip ?? 'not-started';
    const progress = this._progress();
    this.saveProgress({
      ...progress,
      moduleProgress: progress.moduleProgress.map((entry) =>
        entry.moduleId === moduleId
          ? { ...entry, status: restoredStatus, statusBeforeSkip: undefined }
          : entry,
      ),
    });
  }

  isLessonDone(moduleId: string, lessonId: string): boolean {
    return this._progress().moduleProgress
      .find((entry) => entry.moduleId === moduleId)
      ?.lectionProgress.find((entry) => entry.lectionId === lessonId)
      ?.status === 'completed';
  }

  getLessonStatus(moduleId: string, lessonId: string): CompletionStatus {
    return this.findLessonProgress(moduleId, lessonId)?.status ?? 'not-started';
  }

  isLessonManuallyCompleted(moduleId: string, lessonId: string): boolean {
    return this.findLessonProgress(moduleId, lessonId)?.completionSource === 'manual';
  }

  markLessonCompleted(moduleId: string, lessonId: string): void {
    const progress = this._progress();
    const moduleProgress = progress.moduleProgress.map((moduleEntry) => {
      if (moduleEntry.moduleId !== moduleId) {
        return moduleEntry;
      }

      return {
        ...moduleEntry,
        lectionProgress: moduleEntry.lectionProgress.map((lessonEntry) =>
          lessonEntry.lectionId === lessonId
            ? {
                ...lessonEntry,
                status: 'completed' as const,
                completionSource: 'automatic' as const,
                lernbausteinProgress: lessonEntry.lernbausteinProgress.map((blockEntry) => ({
                  ...blockEntry,
                  status: 'completed' as const,
                  completionSource: 'automatic' as const,
                  viewedAt: blockEntry.viewedAt ?? new Date(),
                })),
              }
            : lessonEntry,
        ),
      };
    });

    this.saveProgress({ ...progress, moduleProgress });
  }

  markLessonCompletedManually(moduleId: string, lessonId: string): void {
    const progress = this._progress();
    const now = new Date();
    const moduleProgress = progress.moduleProgress.map((moduleEntry) =>
      moduleEntry.moduleId !== moduleId ? moduleEntry : {
        ...moduleEntry,
        lectionProgress: moduleEntry.lectionProgress.map((lessonEntry) =>
          lessonEntry.lectionId !== lessonId ? lessonEntry : {
            ...lessonEntry,
            status: 'completed' as const,
            completionSource: 'manual' as const,
            lernbausteinProgress: lessonEntry.lernbausteinProgress.map((blockEntry) =>
              blockEntry.status === 'completed' ? blockEntry : {
                ...blockEntry,
                status: 'completed' as const,
                completionSource: 'manual' as const,
                viewedAt: blockEntry.viewedAt ?? now,
              },
            ),
          },
        ),
      },
    );
    const lessonTaskIds = new Set(
      (this.courseStructure.tasks ?? [])
        .filter((task) => task.moduleId === moduleId && task.lessonId === lessonId)
        .map((task) => task.id),
    );
    const tasks = progress.tasks.map((task) =>
      lessonTaskIds.has(task.taskId) && task.status !== 'completed' ? {
        ...task,
        status: 'completed' as const,
        completionSource: 'manual-lesson' as const,
        completedAt: now,
      } : task,
    );
    this.saveProgress({ ...progress, moduleProgress, tasks });
  }

  resetManualLessonCompletion(moduleId: string, lessonId: string): void {
    const progress = this._progress();
    const moduleProgress = progress.moduleProgress.map((moduleEntry) =>
      moduleEntry.moduleId !== moduleId ? moduleEntry : {
        ...moduleEntry,
        lectionProgress: moduleEntry.lectionProgress.map((lessonEntry) => {
          if (lessonEntry.lectionId !== lessonId || lessonEntry.completionSource !== 'manual') {
            return lessonEntry;
          }
          const lernbausteinProgress = lessonEntry.lernbausteinProgress.map((blockEntry) =>
            blockEntry.completionSource === 'manual' ? {
              ...blockEntry,
              status: 'not-started' as const,
              completionSource: undefined,
              viewedAt: undefined,
            } : blockEntry,
          );
          const completed = lernbausteinProgress.length > 0
            && lernbausteinProgress.every((block) => block.status === 'completed');
          const started = lernbausteinProgress.some((block) => block.status !== 'not-started');
          return {
            ...lessonEntry,
            status: completed ? 'completed' as const
              : started ? 'in-progress' as const : 'not-started' as const,
            completionSource: completed ? 'automatic' as const : undefined,
            lernbausteinProgress,
          };
        }),
      },
    );
    const tasks = progress.tasks.map((task) =>
      task.completionSource === 'manual-lesson'
        && (this.courseStructure.tasks ?? []).some(
          (definition) => definition.id === task.taskId
            && definition.moduleId === moduleId
            && definition.lessonId === lessonId,
        )
        ? { ...task, status: 'open' as const, completionSource: undefined, completedAt: undefined }
        : task,
    );
    this.saveProgress({ ...progress, moduleProgress, tasks });
  }

  getLernbausteinStatus(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
  ): CompletionStatus {
    return this.findLessonProgress(moduleId, lessonId)
      ?.lernbausteinProgress.find((entry) => entry.lernbausteinId === lernbausteinId)
      ?.status ?? 'not-started';
  }

  getLessonResumeIndex(
    moduleId: string,
    lessonId: string,
    lernbausteinIds: readonly string[],
  ): number {
    const progress = this.findLessonProgress(moduleId, lessonId);
    if (!progress || lernbausteinIds.length === 0) {
      return 0;
    }

    const activeId = this._activity().activeLernbausteinIds[lessonId];
    const activeIndex = activeId ? lernbausteinIds.indexOf(activeId) : -1;
    if (activeIndex >= 0) {
      return activeIndex;
    }

    const latestActive = progress.lernbausteinProgress
      .filter((entry) => entry.status === 'in-progress' && entry.viewedAt)
      .sort((left, right) => right.viewedAt!.getTime() - left.viewedAt!.getTime())[0];
    if (latestActive) {
      return Math.max(0, lernbausteinIds.indexOf(latestActive.lernbausteinId));
    }

    const firstOpenIndex = lernbausteinIds.findIndex(
      (id) => this.getLernbausteinStatus(moduleId, lessonId, id) !== 'completed',
    );
    return firstOpenIndex >= 0 ? firstOpenIndex : lernbausteinIds.length - 1;
  }

  getActiveLernbausteinId(lessonId: string): string | null {
    return this._activity().activeLernbausteinIds[lessonId] ?? null;
  }

  getLastCourseLocation(): LastCourseLocation | null {
    return this._activity().lastCourseLocation;
  }

  requestLernbausteinOpen(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
    targetId?: string,
  ): void {
    this.lernbausteinOpenRequest.set({
      moduleId,
      lessonId,
      lernbausteinId,
      targetId,
      requestId: ++this.nextLernbausteinRequestId,
    });
  }

  markLernbausteinVisited(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
  ): void {
    this.updateActivity({
      activeLernbausteinIds: {
        ...this._activity().activeLernbausteinIds,
        [lessonId]: lernbausteinId,
      },
      lastCourseLocation: {
        courseId: this._courseContext(),
        moduleId,
        lessonId,
        blockId: lernbausteinId,
      },
    });
    this.updateLernbausteinProgress(
      moduleId,
      lessonId,
      (entry) => entry.lernbausteinId === lernbausteinId && entry.status !== 'completed'
        ? { ...entry, status: 'in-progress', viewedAt: new Date() }
        : entry,
    );
  }

  markLernbausteinCompleted(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
  ): void {
    this.updateLernbausteinProgress(
      moduleId,
      lessonId,
      (entry) => entry.lernbausteinId === lernbausteinId
        ? {
            ...entry,
            status: 'completed',
            completionSource: 'automatic',
            viewedAt: entry.viewedAt ?? new Date(),
          }
        : entry,
    );
  }

  markLernbausteineSkipped(
    moduleId: string,
    lessonId: string,
    lernbausteinIds: readonly string[],
  ): void {
    const skippedIds = new Set(lernbausteinIds);
    this.updateLernbausteinProgress(
      moduleId,
      lessonId,
      (entry) => skippedIds.has(entry.lernbausteinId) && entry.status === 'not-started'
        ? { ...entry, status: 'skipped' }
        : entry,
    );
  }

  markModuleVisited(moduleId: string): void {
    if (!this._activity().visitedModuleIds.includes(moduleId)) {
      this.updateActivity({
        visitedModuleIds: [...this._activity().visitedModuleIds, moduleId],
      });
    }
    if (this.getModuleStatus(moduleId) === 'not-started') {
      this.setModuleStatus(moduleId, 'in-progress');
    }
  }

  isSubtaskDone(moduleId: string, taskIndex: number): boolean {
    return this._activity().completedSubtasks[moduleId]?.includes(taskIndex) ?? false;
  }

  areModuleSubtasksDone(moduleId: string, taskCount: number): boolean {
    if (taskCount === 0) {
      return true;
    }
    return Array.from({ length: taskCount }).every((_, index) => this.isSubtaskDone(moduleId, index));
  }

  setSubtaskDone(moduleId: string, taskIndex: number, done: boolean): void {
    if (taskIndex < 0) {
      return;
    }

    const indexes = new Set(this._activity().completedSubtasks[moduleId] ?? []);
    done ? indexes.add(taskIndex) : indexes.delete(taskIndex);
    this.updateActivity({
      completedSubtasks: {
        ...this._activity().completedSubtasks,
        [moduleId]: [...indexes].sort((a, b) => a - b),
      },
    });

    if (!done && this.getModuleStatus(moduleId) === 'completed') {
      this.setModuleStatus(moduleId, 'in-progress');
    }
  }

  setParticipationStatus(status: ParticipationStatus): void {
    this._participationStatus.set(status);
    this._hasVoucherAnswer.set(null);
  }

  setHasVoucherAnswer(value: boolean): void {
    this._hasVoucherAnswer.set(value);
  }

  validateVoucher(code: string): boolean {
    if (code.trim() !== VALID_VOUCHER_CODE) {
      return false;
    }

    this.voucherValidated.set(true);
    this.updateActivity({ voucherValidated: true });
    return true;
  }

  setAccountSetupPath(path: AccountSetupPath): void {
    if (this.accountSetupPath() !== path) {
      this.resetAccountSetupProgress();
    }

    const visibilityConfirmed =
      path === 'existing-beginner' || path === 'existing-experienced'
        ? this.githubVisibilityConfirmed()
        : false;

    this.accountSetupPath.set(path);
    this.githubVisibilityConfirmed.set(visibilityConfirmed);
    this.updateActivity({
      accountSetupPath: path,
      githubVisibilityConfirmed: visibilityConfirmed,
    });
  }

  setRepositoryExperience(experience: 'beginner' | 'experienced'): void {
    const path: AccountSetupPath =
      experience === 'beginner' ? 'existing-beginner' : 'existing-experienced';
    if (this.accountSetupPath() !== path) {
      this.resetAccountSetupProgress();
    }
    this.accountSetupPath.set(path);
    this.githubVisibilityConfirmed.set(true);
    this.updateActivity({
      accountSetupPath: path,
      githubVisibilityConfirmed: true,
    });
  }

  confirmGithubVisibility(confirmed: boolean): void {
    this.githubVisibilityConfirmed.set(confirmed);
    this.updateActivity({ githubVisibilityConfirmed: confirmed });
  }

  canCompleteAccountSetup(): boolean {
    const path = this.accountSetupPath();
    if (path === null || path === 'existing') {
      return false;
    }
    return path === 'existing-beginner' || path === 'existing-experienced'
      ? this.githubVisibilityConfirmed()
      : true;
  }

  resetAccountSetupToNewPath(): void {
    const path: AccountSetupPath =
      this.accountSetupPath() === 'existing-experienced' ? 'new-skip' : 'new';
    this.resetAccountSetupProgress();
    this.accountSetupPath.set(path);
    this.githubVisibilityConfirmed.set(false);
    this.updateActivity({
      accountSetupPath: path,
      githubVisibilityConfirmed: false,
    });
  }

  getProgressPercent(): number {
    const moduleCount = this._progress().moduleProgress.length;
    return moduleCount > 0 ? (this.getCompletedCount() / moduleCount) * 100 : 0;
  }

  getTaskStatus(taskId: string): TaskStatus {
    return this._progress().tasks.find((task) => task.taskId === taskId)?.status ?? 'open';
  }

  setTaskCompleted(taskId: string, completed: boolean): void {
    const progress = this._progress();
    const now = new Date();
    const tasks = progress.tasks.map((task) => task.taskId !== taskId ? task : {
      ...task,
      status: completed ? 'completed' as const : 'open' as const,
      completionSource: completed ? 'user' as const : undefined,
      startedAt: completed ? task.startedAt ?? now : task.startedAt,
      completedAt: completed ? now : undefined,
    });
    this.saveProgress({ ...progress, tasks });
  }

  getCompetencyStatus(competencyId: string): CompetencyProgressStatus {
    const definition = (this.courseStructure.competencyLevels ?? [])
      .flatMap((entry) => entry.competencies.map((competency) => ({
        ...competency,
        level: entry.level,
      })))
      .find((competency) => competency.id === competencyId);
    if (!definition || !this.isCompetencyLevelUnlocked(definition.level)) {
      return 'locked';
    }
    return definition.taskIds.length > 0
      && definition.taskIds.every((taskId) => this.isTaskSatisfied(taskId))
      ? 'achieved'
      : 'available';
  }

  isCompetencyLevelUnlocked(level: number): boolean {
    if (level <= 1) {
      return true;
    }
    return (this.courseStructure.competencyLevels ?? [])
      .filter((entry) => entry.level < level)
      .flatMap((entry) => entry.competencies)
      .every((competency) => this.getCompetencyStatusIgnoringLock(competency.taskIds) === 'achieved');
  }

  getAchievedCompetencyIds(): string[] {
    return (this.courseStructure.competencyLevels ?? [])
      .flatMap((entry) => entry.competencies)
      .filter((competency) => this.getCompetencyStatus(competency.id) === 'achieved')
      .map((competency) => competency.id);
  }

  getLatestVisitedLesson(
    structure: CourseProgressStructure,
  ): LatestVisitedLessonLocation | null {
    let latest: LatestVisitedLessonLocation | null = null;

    for (const module of structure.modules) {
      const moduleProgress = this._progress().moduleProgress.find(
        (entry) => entry.moduleId === module.id,
      );
      if (!moduleProgress) {
        continue;
      }

      for (const lesson of module.lessons) {
        const lessonProgress = moduleProgress.lectionProgress.find(
          (entry) => entry.lectionId === lesson.id,
        );
        if (!lessonProgress) {
          continue;
        }

        for (const block of lessonProgress.lernbausteinProgress) {
          if (!block.viewedAt) {
            continue;
          }
          if (!latest || block.viewedAt.getTime() > (latest.viewedAt?.getTime() ?? 0)) {
            latest = {
              moduleId: module.id,
              lessonId: lesson.id,
              viewedAt: block.viewedAt,
            };
          }
        }
      }
    }

    if (latest) {
      return latest;
    }

    for (const module of structure.modules) {
      for (const lesson of module.lessons) {
        if (this.getActiveLernbausteinId(lesson.id)) {
          return {
            moduleId: module.id,
            lessonId: lesson.id,
            viewedAt: null,
          };
        }
      }
    }

    return null;
  }

  configureCourseStructure(structure: CourseProgressStructure): void {
    this.courseStructure = structure;
    const current = this._progress();
    const existingModules = new Map<string, LearnerCourseProgress['moduleProgress'][number]>(
      current.moduleProgress.map((module) => [module.moduleId, module]),
    );
    const moduleProgress = structure.modules.map((module) => {
      const existingModule = existingModules.get(module.id);
      const existingLessons = new Map<
        string,
        LearnerCourseProgress['moduleProgress'][number]['lectionProgress'][number]
      >(
        (existingModule?.lectionProgress ?? []).map((lesson) => [lesson.lectionId, lesson]),
      );
      const lectionProgress = module.lessons.map((lesson) => {
        const existingLesson = existingLessons.get(lesson.id);
        const existingBlocks = new Map<
          string,
          LearnerCourseProgress['moduleProgress'][number]['lectionProgress'][number]['lernbausteinProgress'][number]
        >(
          (existingLesson?.lernbausteinProgress ?? []).map((block) => [
            block.lernbausteinId,
            block,
          ]),
        );
        return {
          lectionId: createStableId(lesson.id),
          status: existingLesson?.status ?? 'not-started' as const,
          completionSource: existingLesson?.completionSource,
          lernbausteinProgress: lesson.blockIds.map((blockId) =>
            existingBlocks.get(blockId) ?? {
              lernbausteinId: createStableId(blockId),
              status: 'not-started' as const,
            },
          ),
        };
      });
      const started = lectionProgress.some((lesson) => lesson.status !== 'not-started');
      const existingStatus = existingModule?.status ?? 'not-started' as const;
      return {
        moduleId: createStableId(module.id),
        status: existingStatus === 'completed' || existingStatus === 'skipped'
          ? existingStatus
          : started
            ? 'in-progress' as const
            : existingStatus,
        lectionProgress,
        statusBeforeSkip: existingModule?.statusBeforeSkip,
        milestoneStatus: existingModule?.milestoneStatus ?? 'not-started' as const,
        milestoneCompletedAt: existingModule?.milestoneCompletedAt,
      };
    });
    const existingTasks = new Map<string, LearnerCourseProgress['tasks'][number]>(
      current.tasks.map((task) => [task.taskId, task]),
    );
    const tasks = structure.tasks === undefined
      ? current.tasks
      : structure.tasks.map((task) => existingTasks.get(task.id) ?? {
          taskId: createStableId(task.id),
          status: 'open' as const,
        });

    this.saveProgress({ ...current, moduleProgress, tasks });
  }

  getQuizState(lernbausteinId: string): QuizState | null {
    return this._activity().quizzes[lernbausteinId] ?? null;
  }

  saveQuizState(lernbausteinId: string, state: QuizState): void {
    this.updateActivity({
      quizzes: {
        ...this._activity().quizzes,
        [lernbausteinId]: state,
      },
    });
  }

  clearQuizState(lernbausteinId: string): void {
    const quizzes = { ...this._activity().quizzes };
    delete quizzes[lernbausteinId];
    this.updateActivity({ quizzes });
  }

  getCategorizationQuizState(lernbausteinId: string): CategorizationQuizState | null {
    return this._activity().categorizationQuizzes[lernbausteinId] ?? null;
  }

  saveCategorizationQuizState(
    lernbausteinId: string,
    state: CategorizationQuizState,
  ): void {
    this.updateActivity({
      categorizationQuizzes: {
        ...this._activity().categorizationQuizzes,
        [lernbausteinId]: state,
      },
    });
  }

  clearCategorizationQuizState(lernbausteinId: string): void {
    const categorizationQuizzes = { ...this._activity().categorizationQuizzes };
    delete categorizationQuizzes[lernbausteinId];
    this.updateActivity({ categorizationQuizzes });
  }

  isExerciseInstructionChecked(
    moduleId: string,
    exerciseIndex: number,
    instructionIndex: number,
  ): boolean {
    return this._activity().exercises[moduleId]?.[exerciseIndex]
      ?.checkedInstructionIndexes.includes(instructionIndex) ?? false;
  }

  setExerciseInstructionChecked(
    moduleId: string,
    exerciseIndex: number,
    instructionIndex: number,
    checked: boolean,
  ): void {
    if (exerciseIndex < 0 || instructionIndex < 0) {
      return;
    }

    const moduleExercises = { ...(this._activity().exercises[moduleId] ?? {}) };
    const existing = moduleExercises[exerciseIndex] ?? {
      checkedInstructionIndexes: [],
      status: null,
    };
    const indexes = new Set(existing.checkedInstructionIndexes);
    checked ? indexes.add(instructionIndex) : indexes.delete(instructionIndex);
    moduleExercises[exerciseIndex] = {
      ...existing,
      checkedInstructionIndexes: [...indexes].sort((a, b) => a - b),
    };

    this.updateActivity({
      exercises: {
        ...this._activity().exercises,
        [moduleId]: moduleExercises,
      },
    });
  }

  getExerciseStatus(moduleId: string, exerciseIndex: number): CourseExerciseStatus {
    return this._activity().exercises[moduleId]?.[exerciseIndex]?.status ?? null;
  }

  setExerciseStatus(
    moduleId: string,
    exerciseIndex: number,
    status: CourseExerciseStatus,
  ): void {
    const moduleExercises = { ...(this._activity().exercises[moduleId] ?? {}) };
    const existing = moduleExercises[exerciseIndex] ?? {
      checkedInstructionIndexes: [],
      status: null,
    };
    moduleExercises[exerciseIndex] = { ...existing, status };
    this.updateActivity({
      exercises: {
        ...this._activity().exercises,
        [moduleId]: moduleExercises,
      },
    });
  }

  private setModuleStatus(moduleId: string, status: CompletionStatus): void {
    const progress = this._progress();
    const moduleProgress = progress.moduleProgress.map((entry) =>
      entry.moduleId === moduleId ? { ...entry, status } : entry,
    );
    this.saveProgress({ ...progress, moduleProgress });
  }

  private isTaskSatisfied(taskId: string): boolean {
    if (this.getTaskStatus(taskId) === 'completed') {
      return true;
    }
    const task = (this.courseStructure.tasks ?? []).find((entry) => entry.id === taskId);
    return task ? this.isModuleSkipped(task.moduleId) : false;
  }

  private getCompetencyStatusIgnoringLock(taskIds: ReadonlyArray<string>): 'available' | 'achieved' {
    return taskIds.length > 0 && taskIds.every((taskId) => this.isTaskSatisfied(taskId))
      ? 'achieved'
      : 'available';
  }

  private findLessonProgress(moduleId: string, lessonId: string) {
    return this._progress().moduleProgress
      .find((entry) => entry.moduleId === moduleId)
      ?.lectionProgress.find((entry) => entry.lectionId === lessonId);
  }

  private updateLernbausteinProgress(
    moduleId: string,
    lessonId: string,
    update: (
      entry: LearnerCourseProgress['moduleProgress'][number]['lectionProgress'][number]['lernbausteinProgress'][number],
    ) => LearnerCourseProgress['moduleProgress'][number]['lectionProgress'][number]['lernbausteinProgress'][number],
  ): void {
    const progress = this._progress();
    const moduleProgress = progress.moduleProgress.map((moduleEntry) => {
      if (moduleEntry.moduleId !== moduleId) {
        return moduleEntry;
      }

      return {
        ...moduleEntry,
        lectionProgress: moduleEntry.lectionProgress.map((lessonEntry) => {
          if (lessonEntry.lectionId !== lessonId) {
            return lessonEntry;
          }

          const lernbausteinProgress = lessonEntry.lernbausteinProgress.map(update);
          const lessonCompleted = lernbausteinProgress.length > 0 &&
            lernbausteinProgress.every((entry) => entry.status === 'completed');
          const lessonStarted = lernbausteinProgress.some((entry) => entry.status !== 'not-started');

          return {
            ...lessonEntry,
            status: lessonCompleted ? 'completed' as const :
              lessonStarted ? 'in-progress' as const : 'not-started' as const,
            lernbausteinProgress,
          };
        }),
      };
    });

    this.saveProgress({ ...progress, moduleProgress });
  }

  recordReviewAnswer(
    questionId: string,
    correct: boolean,
    context: { moduleId?: string; competencyId?: string } = {},
  ): void {
    const review = this._activity().review;
    const previous = review.questionStats[questionId];
    const stats: ReviewQuestionStats = {
      attempts: (previous?.attempts ?? 0) + 1,
      correct: (previous?.correct ?? 0) + (correct ? 1 : 0),
      lastAnsweredAt: new Date().toISOString(),
      lastCorrect: correct,
      moduleId: context.moduleId ?? previous?.moduleId,
      competencyId: context.competencyId ?? previous?.competencyId,
    };
    this.updateActivity({
      review: { ...review, questionStats: { ...review.questionStats, [questionId]: stats } },
    });
  }

  getReviewQuestionStats(questionId: string): ReviewQuestionStats | null {
    return this._activity().review.questionStats[questionId] ?? null;
  }

  getCompletedReviewRounds(dateKey: string): number {
    return this._activity().review.completedRounds[dateKey] ?? 0;
  }

  markReviewRoundCompleted(dateKey: string): void {
    const review = this._activity().review;
    this.updateActivity({
      review: {
        ...review,
        completedRounds: {
          ...review.completedRounds,
          [dateKey]: (review.completedRounds[dateKey] ?? 0) + 1,
        },
      },
    });
  }

  private saveProgress(progress: LearnerCourseProgress): void {
    const next: LearnerCourseProgress = { ...progress, version: 2, lastAccessedAt: new Date() };
    this._progress.set(next);
    this.storage.saveCourseProgress(next);
  }

  private loadOrCreateProgress(courseId: StableId): LearnerCourseProgress {
    return this.storage.loadCourseProgress(courseId) ?? {
      courseId,
      version: 2,
      startedAt: new Date(),
      lastAccessedAt: new Date(),
      moduleProgress: [],
      tasks: [],
    };
  }

  private resetAccountSetupProgress(): void {
    const affectedModuleIds = Object.keys(this._activity().completedSubtasks);
    this.updateActivity({ completedSubtasks: {} });
    affectedModuleIds.forEach((moduleId) => this.setModuleStatus(moduleId, 'in-progress'));
  }

  private syncActivitySignals(): void {
    const activity = this._activity();
    this.accountSetupPath.set(activity.accountSetupPath);
    this.githubVisibilityConfirmed.set(activity.githubVisibilityConfirmed);
    this.voucherValidated.set(activity.voucherValidated);
  }

  private updateActivity(patch: Partial<LearnerActivityState>): void {
    const next = { ...this._activity(), ...patch };
    this._activity.set(next);
    this.storage.saveCourseActivity(this._courseContext(), next as unknown as Record<string, unknown>);
  }

  private loadActivity(courseId: string): LearnerActivityState {
    const defaults: LearnerActivityState = {
      voucherValidated: false,
      accountSetupPath: null,
      githubVisibilityConfirmed: false,
      visitedModuleIds: [],
      completedSubtasks: {},
      activeLernbausteinIds: {},
      lastCourseLocation: null,
      quizzes: {},
      categorizationQuizzes: {},
      exercises: {},
      review: { questionStats: {}, completedRounds: {} },
    };
    const stored = this.storage.loadCourseActivity(courseId);
    if (!stored) {
      return defaults;
    }

    return {
      ...defaults,
      ...stored,
      visitedModuleIds: Array.isArray(stored['visitedModuleIds'])
        ? stored['visitedModuleIds'].filter((entry): entry is string => typeof entry === 'string')
        : defaults.visitedModuleIds,
      completedSubtasks: isRecord(stored['completedSubtasks'])
        ? stored['completedSubtasks'] as Record<string, number[]>
        : defaults.completedSubtasks,
      activeLernbausteinIds: isRecord(stored['activeLernbausteinIds'])
        ? stored['activeLernbausteinIds'] as Record<string, string>
        : defaults.activeLernbausteinIds,
      lastCourseLocation: isLastCourseLocation(stored['lastCourseLocation'], courseId)
        ? {
            ...stored['lastCourseLocation'],
            courseId: normalizeCourseId(courseId),
          }
        : defaults.lastCourseLocation,
      quizzes: isRecord(stored['quizzes'])
        ? stored['quizzes'] as Record<string, QuizState>
        : defaults.quizzes,
      categorizationQuizzes: isRecord(stored['categorizationQuizzes'])
        ? stored['categorizationQuizzes'] as Record<string, CategorizationQuizState>
        : defaults.categorizationQuizzes,
      exercises: isRecord(stored['exercises'])
        ? stored['exercises'] as ExerciseStateMap
        : defaults.exercises,
      review: isRecord(stored['review'])
        ? {
            ...defaults.review,
            ...stored['review'],
            questionStats: isRecord(stored['review']['questionStats'])
              ? stored['review']['questionStats'] as Record<string, ReviewQuestionStats>
              : defaults.review.questionStats,
            completedRounds: isRecord(stored['review']['completedRounds'])
              ? stored['review']['completedRounds'] as Record<string, number>
              : defaults.review.completedRounds,
          }
        : defaults.review,
    } as LearnerActivityState;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isLastCourseLocation(
  value: unknown,
  courseId: string,
): value is LastCourseLocation {
  return isRecord(value)
    && normalizeCourseId(String(value['courseId'] ?? '')) === normalizeCourseId(courseId)
    && typeof value['moduleId'] === 'string'
    && typeof value['lessonId'] === 'string'
    && typeof value['blockId'] === 'string';
}
